import { useMemo, useCallback } from 'react';
import type { SavedItem } from '@/hooks/useSavedItems';
import type { SavedProduct } from '@/types/product';
import { savedItemToProduct, getItemSourceType, type ItemSourceType } from '@/utils/savedItemToProduct';

/**
 * Filter types for saved items
 */
export type SavedFilterType = 
  | 'all'
  | 'price-drops'
  | 'back-in-stock'
  | `category:${string}`
  | `retailer:${string}`
  | `source:${ItemSourceType}`;

/**
 * Category icons mapping
 */
const CATEGORY_ICONS: Readonly<Record<string, string>> = {
  shoes: '👟',
  tops: '👕',
  bottoms: '👖',
  outerwear: '🧥',
  dresses: '👗',
  accessories: '⌚',
  bags: '👜',
  underwear: '🧦',
  sportswear: '🏃',
  beauty: '💄',
  tech: '📱',
  home: '🏠',
  books: '📚',
  food: '🍕',
  drinks: '🥤',
  fitness: '💪',
  travel: '✈️',
  entertainment: '🎬',
  toys: '🧸',
  fashion: '👗',
  shopping: '🛍️',
  social: '📲',
  other: '📦',
} as const;

/**
 * Source labels mapping
 */
const SOURCE_LABELS: Readonly<Record<ItemSourceType, string>> = {
  social: 'Social',
  shopping: 'Shopping Sites',
  uploads: 'Uploaded Photos',
  scanned: 'Scanned Items',
} as const;

interface UseSavesFiltersResult {
  readonly priceDropCount: number;
  readonly backInStockCount: number;
  readonly allProducts: SavedProduct[];
  readonly filteredProducts: SavedProduct[];
  readonly getFilterLabel: () => string;
}

export function useSavesFilters(
  items: readonly SavedItem[],
  savedFilter: string,
  hideOrganised: boolean = false,
  itemsInCollections: Set<string> = new Set(),
): UseSavesFiltersResult {
  // Compute alert counts
  const priceDropCount = useMemo(
    () => items.filter(item => (item.price_drop_percent ?? 0) > 0).length,
    [items]
  );

  const backInStockCount = useMemo(
    () => items.filter(item => item.back_in_stock_at !== null).length,
    [items]
  );

  // Group saved items by source
  const sourceGroups = useMemo(() => {
    const groups = new Map<ItemSourceType, readonly SavedItem[]>();
    
    for (const item of items) {
      const source = getItemSourceType(item);
      const existing = groups.get(source) ?? [];
      groups.set(source, [...existing, item]);
    }
    
    return groups;
  }, [items]);

  // Group items by retailer
  const retailerGroups = useMemo(() => {
    const groups = new Map<string, readonly SavedItem[]>();
    
    for (const item of items) {
      const storeName = normalizeStoreName(item.store, item.url);
      if (storeName) {
        const existing = groups.get(storeName) ?? [];
        groups.set(storeName, [...existing, item]);
      }
    }
    
    return groups;
  }, [items]);

  // Convert to product format
  const allProducts = useMemo(
    () => items.map(savedItemToProduct),
    [items]
  );

  // Apply filters
  const filteredProducts = useMemo((): SavedProduct[] => {
    if (savedFilter === 'all') return allProducts;
    
    // Price drops filter
    if (savedFilter === 'price-drops') {
      return allProducts.filter(product => {
        const item = items.find(i => i.id === product.id);
        return (item?.price_drop_percent ?? 0) > 0;
      });
    }
    
    // Back in stock filter
    if (savedFilter === 'back-in-stock') {
      return allProducts.filter(product => {
        const item = items.find(i => i.id === product.id);
        return item?.back_in_stock_at !== null && item?.back_in_stock_at !== undefined;
      });
    }
    
    // Semantic search filter - comma-separated IDs
    if (savedFilter.startsWith('search:')) {
      const matchingIds = savedFilter.replace('search:', '').split(',').filter(Boolean);
      const idSet = new Set(matchingIds);
      // Preserve the order from semantic search results
      return matchingIds
        .map(id => allProducts.find(p => p.id === id))
        .filter((p): p is SavedProduct => p !== undefined);
    }
    
    if (savedFilter.startsWith('category:')) {
      const category = savedFilter.replace('category:', '');
      return allProducts.filter(product => {
        const item = items.find(i => i.id === product.id);
        return (item?.ai_category ?? 'other') === category;
      });
    }
    
    if (savedFilter.startsWith('retailer:')) {
      const retailerName = savedFilter.replace('retailer:', '');
      const retailerItems = retailerGroups.get(retailerName);
      if (!retailerItems) return [];
      const retailerIds = new Set(retailerItems.map(i => i.id));
      return allProducts.filter(product => retailerIds.has(product.id));
    }
    
    if (savedFilter.startsWith('source:')) {
      const sourceKey = savedFilter.replace('source:', '') as ItemSourceType;
      const sourceItems = sourceGroups.get(sourceKey);
      if (!sourceItems) return [];
      const sourceIds = new Set(sourceItems.map(i => i.id));
      return allProducts.filter(product => sourceIds.has(product.id));
    }
    
    return allProducts;
  }, [allProducts, savedFilter, items, sourceGroups, retailerGroups]);

  // Apply hide organised filter on top of other filters
  const finalProducts = useMemo((): SavedProduct[] => {
    if (!hideOrganised || itemsInCollections.size === 0) return filteredProducts;
    return filteredProducts.filter(product => !itemsInCollections.has(product.id));
  }, [filteredProducts, hideOrganised, itemsInCollections]);

  // Generate filter label
  const getFilterLabel = useCallback((): string => {
    if (savedFilter === 'all') return 'All';
    if (savedFilter === 'price-drops') return '🔥 On Sale';
    if (savedFilter === 'back-in-stock') return '📦 Back in Stock';
    
    // Semantic search filter
    if (savedFilter.startsWith('search:')) {
      const count = savedFilter.replace('search:', '').split(',').filter(Boolean).length;
      return `🔍 ${count} matches`;
    }
    
    if (savedFilter.startsWith('category:')) {
      const cat = savedFilter.replace('category:', '');
      const icon = CATEGORY_ICONS[cat] ?? '📦';
      return `${icon} ${capitalizeFirst(cat)}`;
    }
    
    if (savedFilter.startsWith('retailer:')) {
      const retailer = savedFilter.replace('retailer:', '');
      return `🏪 ${retailer}`;
    }
    
    if (savedFilter.startsWith('source:')) {
      const src = savedFilter.replace('source:', '') as ItemSourceType;
      return SOURCE_LABELS[src] ?? src;
    }
    
    return 'All';
  }, [savedFilter]);

  return {
    priceDropCount,
    backInStockCount,
    allProducts,
    filteredProducts: finalProducts,
    getFilterLabel,
  };
}

/**
 * Normalize store name from item data
 */
function normalizeStoreName(store: string | null, url: string | null): string | null {
  if (store) {
    return capitalizeFirst(store.toLowerCase());
  }
  
  if (url) {
    try {
      const hostname = new URL(url).hostname.replace('www.', '');
      const domain = hostname.split('.')[0];
      return capitalizeFirst(domain);
    } catch {
      return null;
    }
  }
  
  return null;
}

/**
 * Capitalize first letter
 */
function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
